import { createContext, useContext, type ComponentType, type ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { CopyButton } from './CopyButton';

/**
 * Width of the label column, shared from `DetailList` to its `DetailField`
 * children so a list with longer labels can widen every row at once. Holds a
 * Tailwind width class (e.g. `w-24`, `w-32`).
 */
const DetailLabelWidthContext = createContext<string>('w-24');

interface DetailListProps {
  children: ReactNode;
  className?: string;
  /** Vertical spacing between rows. Defaults to `cozy`. */
  spacing?: 'cozy' | 'dense';
  /**
   * Tailwind width class for the label column, applied to every child
   * `DetailField`. Defaults to `w-24`. Widen it (e.g. `w-32`) when labels
   * would otherwise truncate.
   */
  labelWidth?: string;
  /**
   * Render rows with alternating background tints (zebra striping) and
   * inset padding so each label/value pair reads as its own band. When set,
   * the `spacing` prop is ignored — striped rows sit flush against each other.
   */
  striped?: boolean;
  'data-testid'?: string;
}

/**
 * Vertical group of `DetailField` rows. Use inside a `SectionCard`,
 * panel, or modal body where you need a consistent list of label/value
 * pairs. The component is presentation-only — it does not own data
 * fetching, headings, or section chrome.
 *
 * ```tsx
 * <DetailList>
 *   <DetailField label="Model" value="claude-sonnet-4" />
 *   <DetailField label="Devbox" value={id} mono copyable />
 *   <DetailField label="Stream"><WorkflowResourceLink type="axon" id={id} /></DetailField>
 * </DetailList>
 * ```
 */
export function DetailList({
  children,
  className,
  spacing = 'cozy',
  labelWidth = 'w-24',
  striped = false,
  'data-testid': dataTestId,
}: DetailListProps) {
  return (
    <DetailLabelWidthContext.Provider value={labelWidth}>
      <div
        data-testid={dataTestId}
        className={cn(
          striped
            ? [
                '[&>*]:rounded-md [&>*]:px-2 [&>*]:transition-colors',
                '[&>*:nth-child(odd)]:bg-muted/40 [&>*:nth-child(odd):hover]:bg-muted/55',
                '[&>*:nth-child(even):hover]:bg-muted/25',
              ]
            : spacing === 'cozy'
              ? 'space-y-1.5'
              : 'space-y-0.5',
          className,
        )}
      >
        {children}
      </div>
    </DetailLabelWidthContext.Provider>
  );
}

interface DetailFieldProps {
  /** Leading label (the row's "key"). */
  label: ReactNode;
  /** Optional icon rendered next to the label. */
  icon?: ComponentType<{ className?: string }>;
  /**
   * Primary value. Ignored when `children` is provided. Strings render
   * inline; everything else renders as-is.
   */
  value?: string | number | null;
  /** Custom value slot — takes precedence over `value`. */
  children?: ReactNode;
  /** Render value in monospace with subtle tone. */
  mono?: boolean;
  /**
   * Show a copy button on the right. Requires `value` to be a string.
   * No-op when used together with `children`.
   */
  copyable?: boolean;
  /**
   * Render the string value as an external link. The trailing
   * external-link glyph is appended automatically.
   */
  href?: string;
  /** Compact row — smaller text and tighter vertical rhythm. */
  dense?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Single label/value row used inside a `DetailList`. Generalizes the
 * older `InfoRow` and ad-hoc `ParamRow` patterns into one primitive
 * that handles the common shapes: plain text, mono identifiers,
 * copy-to-clipboard, external links, and custom value slots.
 */
export function DetailField({
  label,
  icon: Icon,
  value,
  children,
  mono = false,
  copyable = false,
  href,
  dense = false,
  className,
  'data-testid': dataTestId,
}: DetailFieldProps) {
  const labelWidthClass = useContext(DetailLabelWidthContext);
  const hasChildren = children !== undefined && children !== null;
  const hasValue = hasChildren || (value !== undefined && value !== null && value !== '');
  if (!hasValue) return null;

  const labelSize = dense ? 'text-2xs' : 'text-xs';
  const valueSize = dense ? 'text-2xs' : 'text-sm';
  const minHeight = dense ? 'min-h-[24px]' : 'min-h-[28px]';

  const showCopy = copyable && typeof value === 'string' && !hasChildren;

  let valueNode: ReactNode;
  if (hasChildren) {
    valueNode = children;
  } else if (href && typeof value === 'string') {
    valueNode = (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'inline-flex items-center gap-1 break-all leading-snug text-status-info hover:underline',
          valueSize,
          mono && 'font-mono',
        )}
      >
        {value}
        <ExternalLink className="h-2.5 w-2.5 opacity-60 shrink-0" aria-hidden="true" />
      </a>
    );
  } else if (mono) {
    valueNode = (
      <code className={cn('font-mono break-all text-foreground/85', valueSize)}>{value}</code>
    );
  } else {
    valueNode = (
      <span className={cn('break-words leading-snug text-foreground/90', valueSize)}>{value}</span>
    );
  }

  return (
    <div data-testid={dataTestId} className={cn('flex items-center gap-3', minHeight, className)}>
      <span
        className={cn(
          'flex shrink-0 items-center gap-1.5 leading-5 text-muted-foreground',
          labelSize,
          labelWidthClass,
        )}
      >
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
        <span className="truncate">{label}</span>
      </span>
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <div className="min-w-0 flex-1 leading-5">{valueNode}</div>
        {showCopy && <CopyButton text={value as string} />}
      </div>
    </div>
  );
}
